import { OpportunitySectionNav } from './OpportunitySectionNav';
import { opportunitySections } from './opportunitySections';
import React, { useState, useEffect } from 'react';
import OpportunityDealProposals from '../proposals/OpportunityDealProposals';
import { 
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  TabPanel,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Avatar,
  Sheet,
  Grid,
  Stack,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/joy';
import {
  ArrowLeft,
  PencilSimple as Edit2,
  Phone,
  Envelope as Mail,
  Calendar,
  CurrencyDollar as DollarSign,
  Target,
  User,
  FileText,
  ChatCircle as MessageSquare,
  Clock,
  TrendUp as TrendingUp,
  XCircle as AlertCircle,
  Image,
  UploadSimple as Upload,
  Cloud,
  MapPin,
  Trash,
} from '@phosphor-icons/react';
import type { OpportunityDetail as OpportunityDetailType } from '../../types';
import { userDisplayName, userInitials } from '../../utils/userDisplay';
import type { Task } from '../../types/tasks';
import { useOpportunityPhotos, useOpportunityDocs } from '../../hooks/useOpportunity';
import { OpportunityPhotoUploadModal } from './OpportunityPhotoUploadModal';
import { OpportunityDocumentUploadModal } from './OpportunityDocumentUploadModal';
import { OpportunityNoteModal } from './OpportunityNoteModal';
import { PhotoGallery } from '../PhotoGallery';
import { DocumentGallery } from './DocumentGallery';
import { TaskCard } from '../tasks/TaskCard';
import { TaskCreateModal } from '../tasks/TaskCreateModal';
import { TaskDetailModal } from '../tasks/TaskDetailModal';
import { MeasurementsList, MeasurementPdfUploadModal } from '../measurements';
import {
  useDetailedMeasurementsByOpportunity,
  useDeleteDetailedMeasurement,
  useMeasurements,
} from '../../hooks/measurements';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { estimatesApi } from '../../lib/billingApi';
import toast from 'react-hot-toast';
import { useTaskMutations } from '../../hooks/useTasks';
import { useNavigate } from 'react-router-dom';
import { useOpportunityUsage } from '../../hooks/useOpportunityUsage';
import { Tooltip } from '@mui/joy';

interface OpportunityDetailComponentProps {
  opportunity: OpportunityDetailType;
  loading?: boolean;
  onBack: () => void;
  onEdit: (opportunity: OpportunityDetailType) => void;
  onNoteAdded?: () => void;
}

export const OpportunityDetail: React.FC<OpportunityDetailComponentProps> = ({
  opportunity,
  loading = false,
  onBack,
  onEdit,
  onNoteAdded,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [generatingWeatherReport, setGeneratingWeatherReport] = useState(false);
  const [fadingTasks, setFadingTasks] = useState<Set<number>>(new Set());
  const [visibleTasks, setVisibleTasks] = useState<Task[]>([]);
  const [measurementUploadOpen, setMeasurementUploadOpen] = useState(false);
  const [replaceUploadMode, setReplaceUploadMode] = useState(false);
  const [replaceConfirmAction, setReplaceConfirmAction] = useState<'upload' | 'quick_measure' | null>(null);
  const [isReplacingMeasurement, setIsReplacingMeasurement] = useState(false);
  const [generatingReportId, setGeneratingReportId] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // Photo and document hooks
  const { data: photos = [], isLoading: photosLoading, refetch: refetchPhotos } = useOpportunityPhotos(opportunity.id.toString());
  const { data: docs = [], isLoading: docsLoading, refetch: refetchDocs } = useOpportunityDocs(opportunity.id.toString());

  // Measurement documents query (documents with measurement=true)
  const { data: measurementDocs = [], isLoading: measurementDocsLoading } = useQuery({
    queryKey: ['opportunityMeasurementDocs', opportunity.id.toString()],
    queryFn: async () => {
      const response = await apiClient.get<any>(`/api/docs/?opportunity=${opportunity.id}&measurement=true`);
      return Array.isArray(response.data) ? response.data : (response.data?.results || []);
    },
  });

  // Detailed measurements query (parsed measurement data)
  const { data: detailedMeasurements = [], isLoading: detailedMeasurementsLoading } = useDetailedMeasurementsByOpportunity(opportunity.id);
  const { data: opportunityMeasurements = [] } = useMeasurements(undefined, opportunity.id);
  const deleteDetailedMeasurementMutation = useDeleteDetailedMeasurement();

  const activeApexMeasurement = opportunityMeasurements[0] ?? null;
  const uploadedDetailedMeasurements = detailedMeasurements.filter(
    (dm) => dm.is_external_upload ?? (!dm.measurement && (Boolean(dm.source_document) || dm.source_type !== 'other'))
  );
  const activeUploadedMeasurement = uploadedDetailedMeasurements[0] ?? null;
  const hasApexMeasurement = Boolean(activeApexMeasurement);
  const hasUploadedMeasurement =
    !hasApexMeasurement && (uploadedDetailedMeasurements.length > 0 || measurementDocs.length > 0);
  const existingMeasurementLabel = hasApexMeasurement
    ? `ApexOS Quick Measure (${
        activeApexMeasurement?.total_area
          ? `${Number(activeApexMeasurement.total_area).toLocaleString()} sq ft`
          : activeApexMeasurement?.address || `#${activeApexMeasurement?.id}`
      })`
    : activeUploadedMeasurement
      ? `${activeUploadedMeasurement.source_type_display}${
          activeUploadedMeasurement.source_document_filename
            ? ` (${activeUploadedMeasurement.source_document_filename})`
            : ''
        }`
      : 'Uploaded Measurement Report';

  // Usage data hook for this specific opportunity
  const { photoUsage, docUsage, refetch: refetchUsage } = useOpportunityUsage(opportunity.id);
  
  // Tasks query
  const { data: tasks = [], isLoading: tasksLoading, refetch: refetchTasks } = useQuery({
    queryKey: ['opportunityTasks', opportunity.id.toString()],
    queryFn: async () => {
      const response = await apiClient.get<Task[]>('/api/tasks/by_opportunity/?opportunity_id=' + opportunity.id);
      return response.data;
    },
  });

  // Sync visibleTasks with tasks data (filter out completed tasks)
  useEffect(() => {
    setVisibleTasks(tasks.filter(t => t.status !== 'completed'));
  }, [tasks]);

  // Task mutations for completing/uncompleting tasks
  const { completeTask: completeTaskMutation, updateStatus: updateStatusMutation } = useTaskMutations();

  const handleTaskToggleComplete = (taskId: number, completed: boolean) => {
    if (completed) {
      // Start fade animation immediately
      setFadingTasks(prev => new Set(prev).add(taskId));

      // Delay mutation to let animation start
      setTimeout(() => {
        completeTaskMutation.mutate(taskId, {
          onSuccess: () => {
            // Wait for animation to complete before removing
            setTimeout(() => {
              setFadingTasks(prev => {
                const newSet = new Set(prev);
                newSet.delete(taskId);
                return newSet;
              });
              setVisibleTasks(prev => prev.filter(t => t.id !== taskId));
              refetchTasks();
            }, 400);
          }
        });
      }, 100);
    } else {
      updateStatusMutation.mutate({ taskId, status: 'not_started' }, {
        onSuccess: () => refetchTasks(),
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const OPPORTUNITY_SOURCES: Record<string, string> = {
    'referral': 'Referral',
    'marketing': 'Marketing Campaign',
    'cold_call': 'Cold Call',
    'door_knocking': 'Door Knocking',
    'inbound': 'Inbound Lead',
    'networking': 'Networking Event',
    'existing_customer': 'Existing Customer',
    'partner': 'Partner Referral',
    'other': 'Other',
  };

  const getSourceLabel = (source: string): string => {
    return OPPORTUNITY_SOURCES[source] || source;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'won':
        return 'success';
      case 'lost':
        return 'danger';
      case 'open':
        return 'primary';
      default:
        return 'neutral';
    }
  };

  const isOverdue = opportunity.expected_close_date && 
    new Date(opportunity.expected_close_date) < new Date() && 
    opportunity.status === 'open';

  // Check if opportunity has an address
  const hasAddress = !!(opportunity.address_1 || opportunity.city);
  
  const handleGenerateWeatherReport = async () => {
    if (!hasAddress) {
      alert('Please add an address to this opportunity before generating a weather report.');
      return;
    }
    
    setGeneratingWeatherReport(true);
    try {
      // Build the location string from address fields
      const locationParts = [];
      if (opportunity.address_1) locationParts.push(opportunity.address_1);
      if (opportunity.address_2) locationParts.push(opportunity.address_2);
      if (opportunity.city || opportunity.state || opportunity.zip_code) {
        const cityStateZip = [
          opportunity.city,
          opportunity.state,
          opportunity.zip_code
        ].filter(Boolean).join(' ');
        if (cityStateZip) locationParts.push(cityStateZip);
      }
      const location = locationParts.join(', ') || opportunity.address || '';
      
      const response = await apiClient.generateWeatherReport(opportunity.id, {
        title: `Weather Report - ${opportunity.name}`,
        type: 'weather_summary',
        location: location,
        analysis_period: '6_months',
      });

      if (response.data?.document_id) {
        await refetchDocs();
        setActiveTab('documents');
        alert(
          response.data.message ||
            'Weather report generated and saved to documents.'
        );
      } else {
        throw new Error('Report completed but no document was created');
      }
    } catch (error) {
      console.error('Failed to generate weather report:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to generate weather report. Please try again.';
      alert(message);
    } finally {
      setGeneratingWeatherReport(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Sheet
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 0,
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              variant="soft"
              color="neutral"
              onClick={onBack}
            >
              <ArrowLeft size={16} />
            </IconButton>
            <Box>
              <Typography level="h2" component="h1">
                {opportunity.name}
              </Typography>
              <Typography level="body-sm" textColor="text.secondary">
                Opportunity #{opportunity.id}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="solid"
              color="neutral"
              startDecorator={<Cloud size={16} />}
              onClick={handleGenerateWeatherReport}
              disabled={generatingWeatherReport || !hasAddress}
              title={!hasAddress ? 'Address required for weather report' : ''}
            >
              {generatingWeatherReport ? 'Generating...' : 'Generate Weather Report'}
            </Button>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<Edit2 size={16} />}
              onClick={() => onEdit(opportunity)}
            >
              Edit Opportunity
            </Button>
          </Box>
        </Box>

        {/* Key Metrics */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Card variant="soft" color="primary" sx={{ flex: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DollarSign size={16} />
                <Box>
                  <Typography level="body-xs" textColor="text.secondary">
                    Value
                  </Typography>
                  <Typography level="title-lg" fontWeight="lg">
                    {formatCurrency(opportunity.estimated_value || opportunity.value || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card variant="soft" color="success" sx={{ flex: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Target size={16} />
                <Box>
                  <Typography level="body-xs" textColor="text.secondary">
                    Probability
                  </Typography>
                  <Typography level="title-lg" fontWeight="lg">
                    {opportunity.probability}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card variant="soft" color="warning" sx={{ flex: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Calendar size={16} />
                <Box>
                  <Typography level="body-xs" textColor="text.secondary">
                    Expected Close
                  </Typography>
                  <Typography 
                    level="title-sm" 
                    fontWeight="lg"
                    textColor={isOverdue ? 'danger.500' : 'text.primary'}
                  >
                    {opportunity.expected_close_date 
                      ? formatDate(opportunity.expected_close_date)
                      : 'Not set'
                    }
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card variant="soft" color="neutral" sx={{ flex: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp size={16} />
                <Box>
                  <Typography level="body-xs" textColor="text.secondary">
                    Stage
                  </Typography>
                  <Typography level="title-sm" fontWeight="lg">
                    {opportunity.board_status_details?.name || opportunity.stage_name || 'No Stage'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Status and Source */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {opportunity.status && (
            <Chip
              size="sm"
              variant="soft"
              color={getStatusColor(opportunity.status)}
            >
              {opportunity.status}
            </Chip>
          )}
          {opportunity.source && (
            <Chip size="sm" variant="outlined" color="neutral">
              {getSourceLabel(opportunity.source)}
            </Chip>
          )}
          {isOverdue && (
            <Chip 
              size="sm" 
              variant="soft" 
              color="danger"
              startDecorator={<AlertCircle size={12} />}
            >
              Overdue
            </Chip>
          )}
        </Box>
      </Sheet>

      {/* Tabbed Content */}
      <Box sx={{ p: 2 }}>
        <Tabs value={activeTab} orientation="vertical" onChange={(_, value) => setActiveTab(value as string)}
          sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '64px minmax(0, 1fr)' }, gap: 2, bgcolor: 'transparent', alignItems: 'start' }}>
          <OpportunitySectionNav active={activeTab} onChange={setActiveTab} photos={photos.length} documents={docs.length} />
          <Box sx={{ minWidth: 0, '& > [role="tabpanel"]': { px: { xs: 0, md: 1 }, pt: 0 } }}>
            <Typography level="h3" component="h2" sx={{ px: { xs: 0, md: 1 }, mb: 2 }}>
              {opportunitySections.find(section => section.key === activeTab)?.title}
            </Typography>

          {/* Overview Tab */}
          <TabPanel value="overview" sx={{ px: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 1fr' }, gap: 3 }}>
              {/* Left Column */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Contact Information */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-md" startDecorator={<User size={16} />} sx={{ mb: 2 }}>
                      Contact Information
                    </Typography>
                    {opportunity.contact_details ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography level="title-sm">
                          {opportunity.contact_details.first_name} {opportunity.contact_details.last_name}
                        </Typography>
                        {opportunity.contact_details.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="sm"
                              variant="soft"
                              color="primary"
                              onClick={() => window.location.href = `tel:${opportunity.contact_details?.phone}`}
                            >
                              <Phone size={14} />
                            </IconButton>
                            <Typography level="body-sm" textColor="text.secondary">
                              {opportunity.contact_details.phone}
                            </Typography>
                          </Box>
                        )}
                        {opportunity.contact_details.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="sm"
                              variant="soft"
                              color="primary"
                              onClick={() => window.location.href = `mailto:${opportunity.contact_details?.email}`}
                            >
                              <Mail size={14} />
                            </IconButton>
                            <Typography level="body-sm" textColor="text.secondary">
                              {opportunity.contact_details.email}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Typography level="body-sm" textColor="text.secondary">
                        No contact information available
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Property Address */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-md" startDecorator={<MapPin size={16} />} sx={{ mb: 2 }}>
                      Property Address
                    </Typography>
                    {opportunity.address || opportunity.address_1 || opportunity.city ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {opportunity.address_1 && (
                          <Typography level="body-sm">
                            {opportunity.address_1}
                          </Typography>
                        )}
                        {opportunity.address_2 && (
                          <Typography level="body-sm">
                            {opportunity.address_2}
                          </Typography>
                        )}
                        {(opportunity.city || opportunity.state || opportunity.zip_code) && (
                          <Typography level="body-sm">
                            {[opportunity.city, opportunity.state, opportunity.zip_code]
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography level="body-sm" textColor="text.secondary">
                        No address specified - required for weather report
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Description */}
                {opportunity.description && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Description
                      </Typography>
                      <Typography level="body-sm">
                        {opportunity.description}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Next Action */}
                {(opportunity.next_step || opportunity.next_action) && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" startDecorator={<Clock size={16} />} sx={{ mb: 2 }}>
                        Next Action
                      </Typography>
                      <Typography level="body-sm" sx={{ mb: 1 }}>
                        {opportunity.next_step || opportunity.next_action}
                      </Typography>
                      {(opportunity.next_step_date || opportunity.next_action_date) && (
                        <Typography level="body-xs" textColor="text.secondary">
                          Due: {formatDate(opportunity.next_step_date || opportunity.next_action_date || '')}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Dates */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      Important Dates
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography level="body-sm">Created:</Typography>
                        <Typography level="body-sm" textColor="text.secondary">
                          {formatDate(opportunity.created_at)}
                        </Typography>
                      </Box>
                      {opportunity.date_stage_update && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography level="body-sm">Last Updated:</Typography>
                          <Typography level="body-sm" textColor="text.secondary">
                            {formatDate(opportunity.date_stage_update)}
                          </Typography>
                        </Box>
                      )}
                      {opportunity.actual_close_date && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography level="body-sm">Closed:</Typography>
                          <Typography level="body-sm" textColor="text.secondary">
                            {formatDate(opportunity.actual_close_date)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* Right Column */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Progress */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      Progress
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography level="body-sm">
                          {opportunity.board_status_details?.name || opportunity.stage_name || 'No Stage'}
                        </Typography>
                        <Typography level="body-sm">
                          {opportunity.probability}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        determinate
                        value={opportunity.probability}
                        color={opportunity.probability > 70 ? 'success' : opportunity.probability > 40 ? 'warning' : 'neutral'}
                      />
                    </Box>
                    <Typography level="body-xs" textColor="text.secondary">
                      Expected value: {formatCurrency((opportunity.estimated_value || opportunity.value || 0) * opportunity.probability / 100)}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Sales team: primary rep plus additional reps */}
                {(opportunity.assigned_to || (opportunity.additional_reps?.length ?? 0) > 0) && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Sales team
                      </Typography>
                      <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                        {[
                          ...(opportunity.assigned_to ? [{ rep: opportunity.assigned_to, primary: true }] : []),
                          ...(opportunity.additional_reps || []).map((rep) => ({ rep, primary: false })),
                        ].map(({ rep, primary }) => (
                          <Box component="li" key={rep.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar size="sm" color={primary ? 'primary' : 'neutral'}>
                              {userInitials(rep)}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography level="body-sm" noWrap>
                                {userDisplayName(rep)}
                              </Typography>
                              <Typography level="body-xs" textColor="text.secondary" noWrap>
                                {rep.email}
                              </Typography>
                            </Box>
                            {primary && (
                              <Chip size="sm" variant="soft" color="primary">
                                Primary
                              </Chip>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Tags */}
                {opportunity.tags && opportunity.tags.length > 0 && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        Tags
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {(opportunity.tags || []).map((tag) => (
                          <Chip
                            key={tag.id}
                            size="sm"
                            variant="solid"
                            sx={{
                              backgroundColor: tag.color,
                              color: '#fff',
                            }}
                          >
                            {tag.name}
                          </Chip>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Box>
          </TabPanel>

          {/* Tasks Tab */}
          <TabPanel value="tasks" sx={{ px: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography level="title-md">
                    Tasks ({tasks.length})
                  </Typography>
                  <Button
                    size="sm"
                    variant="solid"
                    onClick={() => setTaskCreateOpen(true)}
                  >
                    Add Task
                  </Button>
                </Box>
                
                {tasksLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : tasks.length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 6,
                    px: 2,
                    backgroundColor: 'background.level1',
                    borderRadius: 'md'
                  }}>
                    <Typography level="body-lg" sx={{ mb: 1 }}>
                      No tasks yet
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Create a task to track work for this opportunity
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {visibleTasks.map((task) => {
                      const isFading = fadingTasks.has(task.id);
                      return (
                        <Box
                          key={task.id}
                          sx={{
                            opacity: isFading ? 0 : 1,
                            transform: isFading ? 'scale(0.95) translateX(-30px)' : 'scale(1) translateX(0)',
                            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                            backgroundColor: isFading ? 'var(--joy-palette-success-100)' : 'transparent',
                            borderRadius: 'sm',
                            overflow: 'hidden',
                          }}
                        >
                          <TaskCard
                            task={task}
                            onClick={() => {
                              setSelectedTask(task);
                              setTaskDetailOpen(true);
                            }}
                            onToggleComplete={handleTaskToggleComplete}
                            variant="full"
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* Photos Tab */}
          <TabPanel value="photos" sx={{ px: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography level="title-md" startDecorator={<Image size={16} />}>
                      Photos
                    </Typography>
                    {photoUsage && (
                      <Chip
                        size="sm"
                        color={
                          photoUsage.is_at_limit ? 'danger' :
                          photoUsage.percentage_used >= 80 ? 'warning' :
                          'success'
                        }
                        variant="soft"
                      >
                        <Tooltip 
                          title={
                            <Box>
                              <Typography level="body-sm" fontWeight="lg">
                                Photo Storage Usage
                              </Typography>
                              <Typography level="body-xs">
                                {photoUsage.current_usage} of {photoUsage.limit === 'Unlimited' ? 'unlimited' : photoUsage.limit} photos used
                              </Typography>
                              {photoUsage.percentage_used > 0 && photoUsage.limit !== 'Unlimited' && (
                                <LinearProgress
                                  determinate
                                  value={Math.min(photoUsage.percentage_used, 100)}
                                  sx={{ mt: 1, height: 4 }}
                                  color={
                                    photoUsage.is_at_limit ? 'danger' :
                                    photoUsage.percentage_used >= 80 ? 'warning' :
                                    'success'
                                  }
                                />
                              )}
                            </Box>
                          }
                          placement="bottom"
                        >
                          <span style={{ cursor: 'help' }}>
                            {photoUsage.current_usage}/{photoUsage.limit === 'Unlimited' ? '∞' : photoUsage.limit}
                          </span>
                        </Tooltip>
                      </Chip>
                    )}
                  </Box>
                  <Button
                    size="sm"
                    variant="solid"
                    startDecorator={<Upload size={16} />}
                    onClick={() => setPhotoUploadOpen(true)}
                  >
                    Upload Photos
                  </Button>
                </Box>
                
                <PhotoGallery
                  photos={photos}
                  loading={photosLoading}
                  onPhotoUpdate={refetchPhotos}
                  onPhotoDelete={refetchPhotos}
                  opportunityId={opportunity.id}
                />
              </CardContent>
            </Card>
          </TabPanel>

          {/* Documents Tab */}
          <TabPanel value="documents" sx={{ px: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography level="title-md" startDecorator={<FileText size={16} />}>
                      Documents
                    </Typography>
                    {docUsage && (
                      <Chip
                        size="sm"
                        color={
                          docUsage.is_at_limit ? 'danger' :
                          docUsage.percentage_used >= 80 ? 'warning' :
                          'success'
                        }
                        variant="soft"
                      >
                        <Tooltip 
                          title={
                            <Box>
                              <Typography level="body-sm" fontWeight="lg">
                                Document Storage Usage
                              </Typography>
                              <Typography level="body-xs">
                                {docUsage.current_usage} of {docUsage.limit === 'Unlimited' ? 'unlimited' : docUsage.limit} documents used
                              </Typography>
                              {docUsage.percentage_used > 0 && docUsage.limit !== 'Unlimited' && (
                                <LinearProgress
                                  determinate
                                  value={Math.min(docUsage.percentage_used, 100)}
                                  sx={{ mt: 1, height: 4 }}
                                  color={
                                    docUsage.is_at_limit ? 'danger' :
                                    docUsage.percentage_used >= 80 ? 'warning' :
                                    'success'
                                  }
                                />
                              )}
                            </Box>
                          }
                          placement="bottom"
                        >
                          <span style={{ cursor: 'help' }}>
                            {docUsage.current_usage}/{docUsage.limit === 'Unlimited' ? '∞' : docUsage.limit}
                          </span>
                        </Tooltip>
                      </Chip>
                    )}
                  </Box>
                  <Button
                    size="sm"
                    variant="solid"
                    startDecorator={<Upload size={16} />}
                    onClick={() => setDocUploadOpen(true)}
                  >
                    Upload Documents
                  </Button>
                </Box>
                
                <DocumentGallery
                  folderParent={{ opportunity: opportunity.id }}
                  documents={docs} 
                  loading={docsLoading}
                  onDocumentUpdate={refetchDocs}
                  onDocumentDelete={refetchDocs}
                />
              </CardContent>
            </Card>
          </TabPanel>

          {/* Proposals & Contracts Tab */}
          <TabPanel value="contracts" sx={{ px: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Proposals Section */}
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-md" startDecorator={<FileText size={16} />} sx={{ mb: 2 }}>
                    Proposals
                  </Typography>
                  {opportunity.proposals && opportunity.proposals.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {opportunity.proposals.map((proposal) => (
                        <Card
                          key={proposal.id}
                          variant="soft"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/crm/proposals/${proposal.id}`)}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                              <Typography level="title-sm">
                                {proposal.title || `Proposal ${proposal.proposal_number}`}
                              </Typography>
                              <Chip
                                size="sm"
                                variant="soft"
                                color={
                                  proposal.status === 'accepted' ? 'success' :
                                  proposal.status === 'declined' ? 'danger' :
                                  proposal.status === 'sent' ? 'primary' :
                                  proposal.status === 'viewed' ? 'warning' :
                                  'neutral'
                                }
                              >
                                {proposal.status}
                              </Chip>
                            </Box>
                            <Typography level="body-sm" sx={{ mb: 1 }}>
                              {proposal.proposal_number}
                            </Typography>
                            <Typography level="body-xs" textColor="text.secondary">
                              Created {formatDate(proposal.proposal_date)}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Typography level="body-sm" textColor="text.secondary">
                      No proposals created yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <OpportunityDealProposals opportunity={opportunity.id} />
              {/* Contracts Section */}
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-md" startDecorator={<FileText size={16} />} sx={{ mb: 2 }}>
                    Contracts
                  </Typography>
                  {opportunity.contracts && opportunity.contracts.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {opportunity.contracts.map((contract) => (
                        <Card key={contract.id} variant="soft">
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                              <Typography level="title-sm">
                                {contract.title}
                              </Typography>
                              <Chip size="sm" variant="outlined" color="neutral">
                                {contract.status}
                              </Chip>
                            </Box>
                            <Typography level="body-sm" sx={{ mb: 1 }}>
                              Contract #{contract.contract_number}
                            </Typography>
                            <Typography level="body-sm" fontWeight="lg">
                              {formatCurrency(contract.total_value || 0)}
                            </Typography>
                            <Typography level="body-xs" textColor="text.secondary">
                              Created {formatDate(contract.created_at)}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Typography level="body-sm" textColor="text.secondary">
                      No contracts created yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </TabPanel>

          {/* Billing Tab */}
          <TabPanel value="billing" sx={{ px: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" startDecorator={<DollarSign size={16} />} sx={{ mb: 2 }}>
                  Estimates
                </Typography>
                {opportunity.estimates && opportunity.estimates.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(opportunity.estimates || []).map((estimate) => (
                      <Card key={estimate.id} variant="soft">
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                            <Typography level="title-sm">
                              Estimate #{estimate.document_number}
                            </Typography>
                            <Chip 
                              size="sm" 
                              variant="outlined" 
                              color={
                                estimate.status === 'sent' ? 'primary' : 
                                estimate.status === 'draft' ? 'neutral' :
                                estimate.status === 'viewed' ? 'success' :
                                estimate.acceptance_status === 'accepted' ? 'success' :
                                estimate.acceptance_status === 'rejected' ? 'danger' :
                                'neutral'
                              }
                            >
                              {estimate.acceptance_status || estimate.status}
                            </Chip>
                          </Box>
                          <Typography level="body-sm" sx={{ mb: 1 }}>
                            {estimate.contact_name}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography level="body-sm" fontWeight="lg">
                              {formatCurrency(parseFloat(estimate.total_amount || '0'))}
                            </Typography>
                            <Typography level="body-xs" textColor="text.secondary">
                              Created {formatDate(estimate.created_at)}
                            </Typography>
                          </Box>
                          {estimate.valid_until && (
                            <Typography level="body-xs" textColor="text.secondary" sx={{ mt: 1 }}>
                              Valid until {formatDate(estimate.valid_until)}
                            </Typography>
                          )}
                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="primary"
                              onClick={() => window.location.href = `/operations/billing/estimates/${estimate.id}`}
                            >
                              View Estimate
                            </Button>
                            {estimate.acceptance_status === 'accepted' && !estimate.converted_to_invoice_id && (
                              <Button
                                size="sm"
                                variant="solid"
                                color="success"
                                onClick={async () => {
                                  try {
                                    const data = await estimatesApi.convertToInvoice(estimate.id);
                                    const invoiceId = data?.invoice?.id ?? data?.invoice_id;
                                    toast.success('Estimate converted to invoice');
                                    if (invoiceId) {
                                      navigate(`/operations/billing/invoices/${invoiceId}`);
                                    }
                                  } catch (error) {
                                    toast.error((error as any)?.error || 'Failed to convert estimate to invoice');
                                  }
                                }}
                              >
                                Convert to Invoice
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography level="body-sm" textColor="text.secondary" sx={{ mb: 2 }}>
                      No estimates created yet.
                    </Typography>
                    <Button
                      size="sm"
                      variant="solid"
                      startDecorator={<DollarSign size={16} />}
                      onClick={() => window.location.href = `/operations/billing/estimates/new?opportunity=${opportunity.id}`}
                    >
                      Create Estimate
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </TabPanel>

          {/* Measurements Tab */}
          <TabPanel value="measurements" sx={{ px: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Typography level="title-md" startDecorator={<MapPin size={16} />}>
                    Measurements
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {hasApexMeasurement && activeApexMeasurement ? (
                      <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        startDecorator={<Edit2 size={16} />}
                        onClick={() =>
                          navigate(
                            `/operations/measurements?opportunityId=${opportunity.id}&measurementId=${activeApexMeasurement.id}`
                          )
                        }
                      >
                        Edit Quick Measure
                      </Button>
                    ) : hasUploadedMeasurement ? (
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<MapPin size={16} />}
                        onClick={() => setReplaceConfirmAction('quick_measure')}
                      >
                        Replace with Quick Measure
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<MapPin size={16} />}
                        onClick={() => navigate(`/operations/measurements?opportunityId=${opportunity.id}`)}
                      >
                        Create Quick Measure
                      </Button>
                    )}

                    {detailedMeasurements.some(dm => dm.parsing_status === 'completed') && (
                      <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        loading={generatingReportId !== null}
                        startDecorator={<FileText size={16} />}
                        onClick={async () => {
                          const dm = detailedMeasurements.find(d => d.parsing_status === 'completed');
                          if (!dm) return;
                          setGeneratingReportId(dm.id);
                          try {
                            const { authService } = await import('../../lib/auth');
                            const token = authService.getAccessToken();
                            const resp = await fetch('/api/integrations/roof-measurement/reports/generate-from-measurement/', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                              },
                              credentials: 'include',
                              body: JSON.stringify({ detailed_measurement_id: dm.id }),
                            });
                            if (!resp.ok) {
                              const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
                              throw new Error(err.error || `HTTP ${resp.status}`);
                            }
                            const blob = await resp.blob();
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            void refetchDocs();
                            void queryClient.invalidateQueries({
                              queryKey: ['opportunityMeasurementDocs', opportunity.id.toString()],
                            });
                          } catch (err) {
                            console.error('Report generation failed:', err);
                            alert(`Failed to generate report: ${err instanceof Error ? err.message : 'Please try again.'}`);
                          } finally {
                            setGeneratingReportId(null);
                          }
                        }}
                      >
                        Generate Roof Report
                      </Button>
                    )}

                    {hasApexMeasurement ? (
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<Upload size={16} />}
                        onClick={() => setReplaceConfirmAction('upload')}
                      >
                        Replace with Uploaded PDF
                      </Button>
                    ) : hasUploadedMeasurement ? (
                      <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        startDecorator={<Upload size={16} />}
                        onClick={() => {
                          setReplaceUploadMode(true);
                          setMeasurementUploadOpen(true);
                        }}
                      >
                        Replace Uploaded PDF
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        startDecorator={<Upload size={16} />}
                        onClick={() => {
                          setReplaceUploadMode(false);
                          setMeasurementUploadOpen(true);
                        }}
                      >
                        Upload Measurements
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Parsed Measurement Data Section */}
                {detailedMeasurementsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                ) : (hasUploadedMeasurement ? uploadedDetailedMeasurements : detailedMeasurements.slice(0, 1)).length > 0 ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                      Measurement Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {(hasUploadedMeasurement ? uploadedDetailedMeasurements.slice(0, 1) : detailedMeasurements.slice(0, 1)).map((dm) => {
                        const isUploadedCard =
                          dm.is_external_upload ?? (!dm.measurement && (Boolean(dm.source_document) || dm.source_type !== 'other'));
                        return (
                          <Card key={dm.id} variant="soft" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography level="title-sm">
                                  {isUploadedCard ? dm.source_type_display : 'ApexOS Quick Measure Summary'}
                                </Typography>
                                <Chip
                                  size="sm"
                                  color={dm.parsing_status === 'completed' ? 'success' : dm.parsing_status === 'failed' ? 'danger' : 'neutral'}
                                  variant="soft"
                                >
                                  {dm.parsing_status_display}
                                </Chip>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {dm.source_document_url && (
                                  <Button
                                    size="sm"
                                    variant="plain"
                                    component="a"
                                    href={dm.source_document_url}
                                    target="_blank"
                                    startDecorator={<FileText size={14} />}
                                  >
                                    View PDF
                                  </Button>
                                )}
                                {isUploadedCard && (
                                  <IconButton
                                    size="sm"
                                    variant="soft"
                                    color="danger"
                                    title="Delete uploaded measurement"
                                    disabled={deleteDetailedMeasurementMutation.isPending}
                                    onClick={async () => {
                                      if (!window.confirm('Are you sure you want to delete this uploaded measurement report?')) {
                                        return;
                                      }
                                      try {
                                        await deleteDetailedMeasurementMutation.mutateAsync({
                                          id: dm.id,
                                          opportunityId: opportunity.id,
                                        });
                                        toast.success('Uploaded measurement deleted');
                                      } catch (err) {
                                        console.error('Failed to delete uploaded measurement:', err);
                                        toast.error('Failed to delete uploaded measurement');
                                      }
                                    }}
                                  >
                                    <Trash size={14} />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>
                            {dm.parsing_status === 'completed' && (
                              <Grid container spacing={2}>
                                <Grid xs={6} sm={3}>
                                  <Typography level="body-xs" textColor="text.tertiary">Total Area</Typography>
                                  <Typography level="body-sm" fontWeight="lg">
                                    {dm.total_area_sqft?.toLocaleString() ?? '-'} sq ft
                                  </Typography>
                                </Grid>
                                <Grid xs={6} sm={3}>
                                  <Typography level="body-xs" textColor="text.tertiary">Squares</Typography>
                                  <Typography level="body-sm" fontWeight="lg">
                                    {dm.total_squares?.toFixed(1) ?? '-'}
                                  </Typography>
                                </Grid>
                                <Grid xs={6} sm={3}>
                                  <Typography level="body-xs" textColor="text.tertiary">Pitch</Typography>
                                  <Typography level="body-sm" fontWeight="lg">
                                    {dm.predominant_pitch || '-'}
                                  </Typography>
                                </Grid>
                                <Grid xs={6} sm={3}>
                                  <Typography level="body-xs" textColor="text.tertiary">Facets</Typography>
                                  <Typography level="body-sm" fontWeight="lg">
                                    {dm.facet_count ?? '-'}
                                  </Typography>
                                </Grid>
                                <Grid xs={6} sm={3}>
                                  <Typography level="body-xs" textColor="text.tertiary">Waste Factor</Typography>
                                  <Typography level="body-sm" fontWeight="lg">
                                    {dm.waste_factor ? `${dm.waste_factor}%` : '-'}
                                  </Typography>
                                </Grid>
                                <Grid xs={6} sm={3}>
                                  <Typography level="body-xs" textColor="text.tertiary">Ridges</Typography>
                                  <Typography level="body-sm">{dm.total_ridges_ft?.toLocaleString() ?? '-'} ft</Typography>
                                </Grid>
                                <Grid xs={6} sm={3}>
                                  <Typography level="body-xs" textColor="text.tertiary">Valleys</Typography>
                                  <Typography level="body-sm">{dm.total_valleys_ft?.toLocaleString() ?? '-'} ft</Typography>
                                </Grid>
                                <Grid xs={6} sm={3}>
                                  <Typography level="body-xs" textColor="text.tertiary">Eaves</Typography>
                                  <Typography level="body-sm">{dm.total_eaves_ft?.toLocaleString() ?? '-'} ft</Typography>
                                </Grid>
                                <Grid xs={6} sm={3}>
                                  <Typography level="body-xs" textColor="text.tertiary">Rakes</Typography>
                                  <Typography level="body-sm">{dm.total_rakes_ft?.toLocaleString() ?? '-'} ft</Typography>
                                </Grid>
                              </Grid>
                            )}
                            {dm.parsing_status === 'failed' && dm.parsing_error && (
                              <Typography level="body-sm" color="danger">
                                Error: {dm.parsing_error}
                              </Typography>
                            )}
                          </Card>
                        );
                      })}
                    </Box>
                  </Box>
                ) : null}

                <Typography level="body-sm" sx={{ mb: 2 }}>
                  Use one measurement source: edit the existing ApexOS measurement, or replace it with an external report.
                  Measurement files below are attachments, not additional values to add to the active calculation.
                </Typography>

                {/* Measurement Documents Section */}
                {measurementDocsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                ) : measurementDocs.length > 0 ? (
                  <Box sx={{ mb: 3 }}>
                    <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                      Measurement Files ({measurementDocs.length})
                    </Typography>
                    <DocumentGallery
                      documents={measurementDocs}
                      opportunityId={opportunity.id}
                      onRefresh={() => {
                        queryClient.invalidateQueries({ queryKey: ['opportunityMeasurementDocs', opportunity.id.toString()] });
                        queryClient.invalidateQueries({ queryKey: ['detailedMeasurementsByOpportunity', opportunity.id] });
                        queryClient.invalidateQueries({ queryKey: ['measurements'] });
                      }}
                    />
                  </Box>
                ) : null}

                {/* Measurements List */}
                <MeasurementsList
                  selectedOpportunityId={opportunity.id}
                  onViewMeasurement={(measurement) => {
                    navigate(`/operations/measurements/${measurement.id}`);
                  }}
                  hideEmptyState={hasUploadedMeasurement || measurementDocs.length > 0}
                />
              </CardContent>
            </Card>
          </TabPanel>

          {/* Notes Tab */}
          <TabPanel value="notes" sx={{ px: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography level="title-md" startDecorator={<MessageSquare size={16} />}>
                    Notes ({opportunity.notes?.length || 0})
                  </Typography>
                  <Button
                    size="sm"
                    variant="solid"
                    startDecorator={<MessageSquare size={16} />}
                    onClick={() => setNoteModalOpen(true)}
                  >
                    Add Note
                  </Button>
                </Box>
                {opportunity.notes && opportunity.notes.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(opportunity.notes || []).map((note) => (
                      <Card key={note.id} variant="soft">
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {note.created_by_details ? (
                                <>
                                  <Avatar size="sm">
                                    {note.created_by_details.first_name[0]}{note.created_by_details.last_name[0]}
                                  </Avatar>
                                  <Typography level="body-sm">
                                    {note.created_by_details.first_name} {note.created_by_details.last_name}
                                  </Typography>
                                </>
                              ) : (
                                <Typography level="body-sm">
                                  {note.created_by_name || 'Unknown'}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {note.is_private && (
                                <Chip size="sm" variant="soft" color="warning">
                                  Private
                                </Chip>
                              )}
                              <Chip 
                                size="sm" 
                                variant="outlined" 
                                color={
                                  note.note_type === 'call' ? 'success' :
                                  note.note_type === 'meeting' ? 'primary' :
                                  note.note_type === 'email' ? 'primary' :
                                  note.note_type === 'follow_up' ? 'warning' :
                                  'neutral'
                                }
                              >
                                {note.note_type_display || note.note_type}
                              </Chip>
                            </Box>
                          </Box>
                          <Typography level="body-sm" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                            {note.content}
                          </Typography>
                          <Typography level="body-xs" textColor="text.secondary">
                            {formatDateTime(note.created_at)}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 6,
                    px: 2,
                    backgroundColor: 'background.level1',
                    borderRadius: 'md'
                  }}>
                    <Typography level="body-lg" sx={{ mb: 1 }}>
                      No notes yet
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Add notes to track important information about this opportunity
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </TabPanel>
          </Box>
        </Tabs>
      </Box>
      
      {/* Photo Upload Modal */}
      <OpportunityPhotoUploadModal
        open={photoUploadOpen}
        onClose={() => {
          setPhotoUploadOpen(false);
          refetchUsage(); // Refresh usage data after upload
        }}
        opportunityId={opportunity.id.toString()}
      />
      
      {/* Document Upload Modal */}
      <OpportunityDocumentUploadModal
        open={docUploadOpen}
        onClose={() => {
          setDocUploadOpen(false);
          refetchUsage(); // Refresh usage data after upload
        }}
        opportunityId={opportunity.id}
      />

      {/* Measurement Upload Modal */}
      <MeasurementPdfUploadModal
        open={measurementUploadOpen}
        onClose={() => {
          setMeasurementUploadOpen(false);
          setReplaceUploadMode(false);
          refetchUsage(); // Refresh usage data after upload
        }}
        opportunityId={opportunity.id}
        replaceExisting={replaceUploadMode || hasApexMeasurement || hasUploadedMeasurement}
        existingMeasurementLabel={
          hasApexMeasurement || hasUploadedMeasurement ? existingMeasurementLabel : undefined
        }
        onSuccess={() => {
          // Refresh the measurement documents after successful upload
          queryClient.invalidateQueries({ queryKey: ['opportunityMeasurementDocs', opportunity.id.toString()] });
          queryClient.invalidateQueries({ queryKey: ['measurements'] });
          queryClient.invalidateQueries({ queryKey: ['detailedMeasurementsByOpportunity', opportunity.id] });
        }}
      />

      {/* Replace Existing Measurement Confirmation Modal */}
      <Modal
        open={replaceConfirmAction !== null}
        onClose={() => {
          if (!isReplacingMeasurement) {
            setReplaceConfirmAction(null);
          }
        }}
      >
        <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 480 }}>
          <ModalClose disabled={isReplacingMeasurement} />
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AlertCircle size={22} />
              Replace Existing Measurement?
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert color="warning" sx={{ mb: 1.5 }}>
              An opportunity can only have either an uploaded measurement report or a single ApexOS Quick Measure.
            </Alert>
            <Typography level="body-sm">
              {replaceConfirmAction === 'upload'
                ? `Proceeding will replace your existing ${existingMeasurementLabel} once you upload a new measurement PDF.`
                : `Proceeding will permanently delete your existing ${existingMeasurementLabel} and open ApexOS Quick Measure for this opportunity.`}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="neutral"
              disabled={isReplacingMeasurement}
              onClick={() => setReplaceConfirmAction(null)}
            >
              Cancel
            </Button>
            {replaceConfirmAction === 'upload' ? (
              <Button
                variant="solid"
                color="warning"
                onClick={() => {
                  setReplaceConfirmAction(null);
                  setReplaceUploadMode(true);
                  setMeasurementUploadOpen(true);
                }}
              >
                Continue to Upload
              </Button>
            ) : (
              <Button
                variant="solid"
                color="warning"
                loading={isReplacingMeasurement}
                onClick={async () => {
                  setIsReplacingMeasurement(true);
                  try {
                    for (const dm of uploadedDetailedMeasurements) {
                      await deleteDetailedMeasurementMutation.mutateAsync({
                        id: dm.id,
                        opportunityId: opportunity.id,
                      });
                    }
                    for (const doc of measurementDocs) {
                      try {
                        await apiClient.delete(`/api/docs/${doc.id}/`);
                      } catch {
                        // Doc may have already been deleted alongside DetailedMeasurement
                      }
                    }
                    await queryClient.invalidateQueries({
                      queryKey: ['opportunityMeasurementDocs', opportunity.id.toString()],
                    });
                    await queryClient.invalidateQueries({
                      queryKey: ['detailedMeasurementsByOpportunity', opportunity.id],
                    });
                    setReplaceConfirmAction(null);
                    navigate(`/operations/measurements?opportunityId=${opportunity.id}&replaceExisting=true`);
                  } catch (err) {
                    console.error('Failed to replace uploaded measurement:', err);
                    toast.error('Failed to remove existing uploaded measurement.');
                  } finally {
                    setIsReplacingMeasurement(false);
                  }
                }}
              >
                Replace & Open Quick Measure
              </Button>
            )}
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Note Modal */}
      <OpportunityNoteModal
        open={noteModalOpen}
        onClose={() => {
          setNoteModalOpen(false);
          // Trigger refetch when note is added
          if (onNoteAdded) {
            onNoteAdded();
          }
        }}
        opportunityId={opportunity.id}
      />
      
      {/* Task Create Modal */}
      <TaskCreateModal
        open={taskCreateOpen}
        onClose={() => setTaskCreateOpen(false)}
        onSuccess={() => {
          setTaskCreateOpen(false);
          refetchTasks();
        }}
        prefilledOpportunityId={opportunity.id}
      />
      
      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          open={taskDetailOpen}
          task={selectedTask}
          onClose={() => {
            setTaskDetailOpen(false);
            setSelectedTask(null);
          }}
          onUpdate={() => refetchTasks()}
          onTaskUpdated={() => refetchTasks()}
        />
      )}
    </Box>
  );
};